import { useMutation, useQuery, useQueryClient } from 'react-query';
import { DefaultValues } from '../../components/common/OnBoarding/defaultValues';
import { AxiosInstance } from '../axios.config';
import { api } from '../constants';

const onBoardUser = (data: DefaultValues) => {
  const formattedData = new FormData();

  formattedData.append('company_name', data?.company_details.name || '');
  formattedData.append('company_address', data?.company_details.address || '');
  formattedData.append('currency', data?.company_details.currency_preference || 'USD');
  if (data?.company_details.company_certificate instanceof File) {
    formattedData.append('certificate_image', data?.company_details.company_certificate);
  }
  if (data?.company_details.pan_vat_certificate instanceof File) {
    formattedData.append('pan_vat_certificate_image', data?.company_details.pan_vat_certificate);
  }
  formattedData.append('shop[0][shop_name]', data?.store_details.name || '');
  formattedData.append('shop[0][phone_number]', data?.store_details.contact_number || '');
  formattedData.append('shop[0][warehouse_location]', data?.store_details.warehouse_address || '');
  if (data?.store_details.store_logo instanceof File) {
    formattedData.append('shop[0][shop_image]', data?.store_details.store_logo);
  }
  formattedData.append('bank_details[account_name]', data?.bank_details.account_holder_name || '');
  formattedData.append('bank_details[bank_name]', data?.bank_details.bank_name || '');
  formattedData.append('bank_details[account_number]', data?.bank_details.account_number || '');
  formattedData.append('bank_details[branch_name]', data?.bank_details.branch_code || '');
  if (data?.bank_details.bank_cheque_book_scan_photo instanceof File) {
    formattedData.append(
      'bank_details[cheque_book_image]',
      data?.bank_details.bank_cheque_book_scan_photo,
    );
  }

  return AxiosInstance.post(api.onBoarding, formattedData);
};

const usePostOnboardingUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'onboarding',
    mutationFn: onBoardUser,
    onSuccess: () => {
      queryClient.invalidateQueries('user');
    },
  });
};

// 👈 New: Function to fetch existing onboarding data
const getOnBoardingUser = () => {
  return AxiosInstance.get(api.onBoarding).then((response) => {
    // 👈 Assumption: Backend returns data in a shape that can be mapped to DefaultValues
    // Adjust mapping based on your API response structure (e.g., response.data.vendor_details)
    const apiData = response.data; // e.g., { company_details: {...}, store_details: {...}, bank_details: {...} }
    // Return in DefaultValues shape for easy form prefill
    return {
      company_details: {
        name: apiData.company_name || '',
        address: apiData.company_address || '',
        currency_preference: apiData.currency || 'USD',
        company_certificate: apiData.certificate_image || null, // 👈 For file fields, you may need to handle URLs or placeholders
        pan_vat_certificate: apiData.pan_vat_certificate_image || null,
      },
      store_details: {
        name: apiData.shop?.[0]?.shop_name || '',
        contact_number: apiData.shop?.[0]?.phone_number || '',
        warehouse_address: apiData.shop?.[0]?.warehouse_location || '',
        store_logo: apiData.shop?.[0]?.shop_image || null,
      },
      bank_details: {
        account_holder_name: apiData.bank_details?.account_name || '',
        bank_name: apiData.bank_details?.bank_name || '',
        account_number: apiData.bank_details?.account_number || '',
        branch_code: apiData.bank_details?.branch_name || '',
        bank_cheque_book_scan_photo: apiData.bank_details?.cheque_book_image || null,
      },
    } as DefaultValues;
  });
};

const useGetOnboardingUser = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['onboarding'],
    queryFn: getOnBoardingUser,
    enabled: options?.enabled ?? true, // 👈 Optional: Enable/disable based on modal open/rejected status
    staleTime: 5 * 60 * 1000, // 👈 5 minutes stale time to reduce refetches
    retry: 1, // 👈 Retry once on failure
  });
};

export { usePostOnboardingUser, useGetOnboardingUser };
